import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MenuController, Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Events } from 'src/app/shared/services/global/events.service';
import { GlobalService, AnalyticsService } from 'src/app/shared/services';
import { EventDataService } from 'src/app/shared/services/eventData/event-data.service';
import { LogoutService } from 'src/app/shared/services/logout/logout.service';
import { DaoGalleryService } from 'src/app/providers/db/dao-gallery.service';
import { GalleryService } from 'src/app/providers/gallery/gallery.service';
import { EventColors } from 'src/app/models/event-colors';
import { IGalleryImage, IGalleryBreadcrumbItem } from 'src/app/models/gallery.models';
import { getGalleryViewMode } from 'src/app/shared/selectors/interactivity.selectors';
import { SetGalleryViewMode } from 'src/app/shared/actions/interactivity.actions';

@Component({
    selector: 'app-gallery-folder',
    templateUrl: './gallery-folder.page.html',
    styleUrls: ['./gallery-folder.page.scss'],
})
export class GalleryFolderPage implements OnInit, OnDestroy {
    private subs: Subscription[] = [];

    eventId: string;
    moduleId: string;
    folderId: string;

    module: any;
    folder: any;
    images: IGalleryImage[] = [];
    loader: boolean = true;
    eventColors: EventColors = new EventColors();
    language: string;
    isMobile: boolean = false;
    backBtn: boolean = true;
    menuBadge: number = 0;

    viewerOpen: boolean = false;
    viewerIndex: number = 0;

    private currentViewMode: 'grid' | 'list' = 'grid';

    get effectiveViewMode(): 'grid' | 'list' {
        return this.isMobile ? this.currentViewMode : 'grid';
    }
    allowGrid: boolean = false;
    allowDownload: boolean = false;
    allowDownloadAll: boolean = false;

    breadcrumbItems: IGalleryBreadcrumbItem[] = [];

    constructor(
        private route: ActivatedRoute,
        public global: GlobalService,
        private events: Events,
        private menuCtrl: MenuController,
        private SAnalytics: AnalyticsService,
        private SEventData: EventDataService,
        private Slogout: LogoutService,
        private daoGallery: DaoGalleryService,
        private galleryService: GalleryService,
        private store: Store,
        private platform: Platform,
    ) {
        this.menuCtrl.enable(true);

        this.eventId = this.route.snapshot.params['eventId'];
        this.moduleId = this.route.snapshot.params['moduleId'];
        this.folderId = this.route.snapshot.params['folderId'];

        this.language = this.SEventData.getLanguage();
        this.menuBadge = this.global.notificationBadge;
        this.events.subscribe('menuBadge', () => {
            this.menuBadge = this.global.notificationBadge;
        });

        this.SAnalytics.goToImagesFolder(this.eventId, this.moduleId, this.folderId);

        const subView = this.store.select(getGalleryViewMode).subscribe(mode => {
            if (mode) this.currentViewMode = mode;
        });
        this.subs.push(subView);
    }

    ngOnInit(): void {
        if (this.platform.is('ios') || this.platform.is('android')) {
            this.isMobile = true;
        }
        if (this.global.previousPage === 'container') {
            this.backBtn = false;
        } else {
            this.backBtn = true;
        }

        this.loadColors();
        this.loadModule();
        this.loadFolder();
        this.loadImages();
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    private loadModule(): void {
        const sub = this.daoGallery.getModule(this.moduleId).subscribe((moduleData) => {
            this.module = moduleData;
            this.updateBreadcrumb();
        });
        this.subs.push(sub);
    }

    private loadFolder(): void {
        const sub = this.daoGallery.getFolder(this.moduleId, this.folderId).subscribe((folderData) => {
            this.folder = folderData;
            if (folderData?.['allowGrid'] !== null && folderData?.['allowGrid'] !== undefined) {
                this.allowGrid = folderData['allowGrid'];
            }
            if (folderData?.['allowDownload'] !== null && folderData?.['allowDownload'] !== undefined) {
                this.allowDownload = folderData['allowDownload'];
            }
            if (folderData?.['allowDownloadAll'] !== null && folderData?.['allowDownloadAll'] !== undefined) {
                this.allowDownloadAll = folderData['allowDownloadAll'];
            }
            this.updateBreadcrumb();
        });
        this.subs.push(sub);
    }

    private loadImages(): void {
        const orderGallery = this.folder?.orderGallery;
        const sub = this.daoGallery.getImages(this.moduleId, this.folderId, orderGallery)
            .pipe(takeUntil(this.Slogout.logoutSubject))
            .subscribe((data: any) => {
                this.images = (data || []).map((item: any) => ({
                    id: item.uid || item.id,
                    uid: item.uid,
                    image: item.url || item.image,
                    thumbnail: item.thumbnail || item.url || item.image,
                    name: item.name,
                    imageName: item.imageName || item.name,
                    type: item.type || 'jpg',
                    order: item.order,
                }));
                this.loader = false;
            });
        this.subs.push(sub);
    }

    private loadColors(): void {
        const sub = this.SEventData.getEventDataListener(this.eventId).subscribe((eventData) => {
            this.eventColors = eventData.colors;
        });
        this.subs.push(sub);
    }

    private updateBreadcrumb(): void {
        this.breadcrumbItems = [];
        if (this.module?.name?.[this.language]) {
            this.breadcrumbItems.push({
                label: this.module.name[this.language],
                route: `/event/${this.eventId}/gallery/${this.moduleId}`,
            });
        }
        if (this.folder?.name?.[this.language]) {
            this.breadcrumbItems.push({
                label: this.folder.name[this.language],
                route: '',
            });
        }
    }

    onBreadcrumbNavigate(item: IGalleryBreadcrumbItem): void {
        // handled by routerLink in breadcrumb
    }

    onToggleViewMode(mode: 'grid' | 'list'): void {
        this.store.dispatch(new SetGalleryViewMode({ viewMode: mode }));
    }

    onImageClick(event: { image: IGalleryImage; index: number }): void {
        this.SAnalytics.imageAccess(this.eventId, event.image);
        this.viewerIndex = event.index;
        this.viewerOpen = true;
    }

    onCloseViewer(): void {
        this.viewerOpen = false;
    }

    onDownloadImage(image: IGalleryImage): void {
        this.galleryService.downloadImage(image, this.eventId);
    }

    async onDownloadSelected(images: IGalleryImage[]): Promise<void> {
        await this.galleryService.downloadSelected(images, this.eventId);
    }

    async onDownloadAll(): Promise<void> {
        await this.galleryService.downloadSelected(this.images, this.eventId);
    }
}
